/**
 * Fechamento do caixa: relatório do que este aparelho validou e a conferência
 * da fila de envio.
 *
 * O caixa só fecha limpo quando tudo subiu. Se sobrar validação no aparelho e
 * ele for guardado, aquela entrada não existe para o resto da operação — por
 * isso a tela tenta enviar antes e, se não conseguir, exige uma confirmação
 * consciente para fechar assim mesmo.
 */

import React, {useCallback, useEffect, useState} from 'react';
import {ActivityIndicator, Alert, Pressable, ScrollView, Text, View} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {ArrowLeft, Clock, CloudUpload} from 'lucide-react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import {ErroApi} from '../api/cliente';
import {caixaAberto, fecharCaixa, type Caixa} from '../caixa';
import {relatorioDoCaixa, type RelatorioCaixa} from '../dados/repositorio';
import {ehEventoDeDemonstracao} from '../dominio/demonstracao';
import {carregarSessao} from '../sessao';
import {sincronizarEvento} from '../sync/sincronizar';
import {comMilhar, horaDe} from '../ui/datas';
import {txt, useIdioma} from '../ui/idioma';
import {estilos, fontes, useTema} from '../ui/tema';
import type {ParamsPilha} from '../navegacao';

type Props = NativeStackScreenProps<ParamsPilha, 'Fechamento'>;

export function TelaFechamentoCaixa({navigation, route}: Props) {
  const {eventoId, eventoNome} = route.params;
  const t = useTema();
  useIdioma();

  const [caixa, setCaixa] = useState<Caixa | null>(null);
  const [relatorio, setRelatorio] = useState<RelatorioCaixa | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    setRelatorio(await relatorioDoCaixa(eventoId));
  }, [eventoId]);

  useEffect(() => {
    caixaAberto().then(setCaixa);
    recarregar();
  }, [recarregar]);

  /** Tenta esvaziar a fila. Devolve quantos ficaram para trás. */
  const enviarPendentes = useCallback(async (): Promise<number> => {
    // Fechar caixa em demonstração não envia nada. Este era o caminho mais
    // perigoso dos cinco: o agendador está desligado no validador, mas o
    // fechamento sincroniza por conta própria — e mandaria as leituras de
    // exemplo para a contagem de um evento de verdade.
    if (ehEventoDeDemonstracao(eventoId)) {
      const so = await relatorioDoCaixa(eventoId);
      setRelatorio(so);
      return 0;
    }

    setEnviando(true);
    setErroEnvio(null);
    try {
      const sessao = await carregarSessao();
      if (sessao) {
        await sincronizarEvento(eventoId, sessao.machineId);
      }
    } catch (e) {
      setErroEnvio(
        e instanceof ErroApi ? e.message : txt('Falha de rede ao enviar as validações'),
      );
    } finally {
      setEnviando(false);
    }

    const atualizado = await relatorioDoCaixa(eventoId);
    setRelatorio(atualizado);
    return atualizado.pendentes;
  }, [eventoId]);

  // Ao abrir a tela, já tenta subir o que falta — quase sempre o operador
  // encontra a fila zerada e fecha sem pensar no assunto.
  useEffect(() => {
    enviarPendentes();
  }, [enviarPendentes]);

  async function fechar() {
    await fecharCaixa();
    navigation.reset({index: 0, routes: [{name: 'Caixa'}]});
  }

  async function tentarFechar() {
    const restantes = await enviarPendentes();

    if (restantes === 0) {
      fechar();
      return;
    }

    const quantas =
      restantes === 1
        ? txt('{quantas} entrada não subiu para o servidor.', {quantas: restantes})
        : txt('{quantas} entradas não subiram para o servidor.', {quantas: restantes});
    Alert.alert(
      txt('Ainda há validações no aparelho'),
      quantas +
        ' ' +
        txt(
          'Se fechar agora, elas só serão enviadas quando este aparelho voltar a abrir este evento com rede.',
        ),
      [
        {text: txt('Tentar de novo'), style: 'cancel', onPress: () => enviarPendentes()},
        {text: txt('Fechar assim mesmo'), style: 'destructive', onPress: fechar},
      ],
    );
  }

  const pendentes = relatorio?.pendentes ?? 0;
  const filaLimpa = pendentes === 0;
  const total = relatorio?.total ?? 0;
  const maior = Math.max(1, ...(relatorio?.porSetor.map(l => l.quantidade) ?? [1]));

  /**
   * O painel do total é escuro nos dois temas. É o número que o operador leva
   * para o supervisor, e sob sol um bloco escuro com número branco é o que
   * ainda se lê na tela do aparelho.
   */
  const painel =
    t.modo === 'dia'
      ? {fundo: t.texto, tinta: t.fundo, fraca: t.textoApagado, divisoria: t.textoMedio}
      : {fundo: t.superficie, tinta: t.texto, fraca: t.textoFraco, divisoria: t.borda};

  return (
    <SafeAreaView style={[estilos.tela, {backgroundColor: t.fundo}]} edges={['top', 'bottom']}>
      <View style={[estilos.cabecalho, {borderBottomColor: t.superficie}]}>
        <Pressable
          onPress={() => navigation.goBack()}
          hitSlop={6}
          accessibilityLabel={txt('Continuar validando')}
          style={[estilos.botaoIcone, {borderColor: t.borda}]}>
          <ArrowLeft size={20} color={t.texto} strokeWidth={2.2} />
        </Pressable>

        <View style={{flex: 1}}>
          <Text style={[estilos.tituloCabecalho, {color: t.texto}]}>{txt('Fechar')}</Text>
          <Text style={[estilos.legendaCabecalho, {color: t.textoFraco}]} numberOfLines={1}>
            {[caixa?.nome, eventoNome].filter(Boolean).join(' · ')}
          </Text>
        </View>
      </View>

      <ScrollView contentContainerStyle={{padding: 20, gap: 14}}>
        <View style={{borderRadius: 20, padding: 24, gap: 6, backgroundColor: painel.fundo}}>
          <Text
            style={[
              estilos.numero,
              {color: painel.tinta, fontSize: 64, lineHeight: 66, fontFamily: fontes.preto, letterSpacing: -3},
            ]}>
            {comMilhar(total)}
          </Text>
          <Text style={{color: painel.fraca, fontSize: 16, fontFamily: fontes.semi}}>
            {total === 1
              ? txt('entrada validada neste aparelho')
              : txt('entradas validadas neste aparelho')}
          </Text>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              marginTop: 10,
              paddingTop: 14,
              borderTopWidth: 1,
              borderTopColor: painel.divisoria,
            }}>
            <Clock size={16} color={painel.fraca} strokeWidth={2.2} />
            <Text
              style={{
                color: painel.fraca,
                fontSize: 14,
                fontFamily: fontes.regular,
                fontVariant: ['tabular-nums'],
              }}>
              {horaDe(relatorio?.primeira)} → {horaDe(relatorio?.ultima)}
              {relatorio?.horaPico !== undefined
                ? txt(' · pico {hora}h', {hora: relatorio.horaPico})
                : ''}
            </Text>
          </View>
        </View>

        <View
          style={{
            borderRadius: 16,
            paddingHorizontal: 18,
            paddingVertical: 16,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            backgroundColor: filaLimpa ? t.sucessoFundo : t.erroFundo,
            borderWidth: 1,
            borderColor: filaLimpa ? t.primaria : t.erroBorda,
          }}>
          <View
            style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: filaLimpa ? t.primaria : t.erro,
            }}>
            <CloudUpload
              size={19}
              color={filaLimpa ? t.tintaPrimaria : t.fundo}
              strokeWidth={2.4}
            />
          </View>

          <View style={{flex: 1, gap: 2}}>
            <Text
              style={{
                color: filaLimpa ? t.sucessoTinta : t.erroTinta,
                fontSize: 16,
                fontFamily: fontes.extra,
                fontVariant: ['tabular-nums'],
              }}>
              {enviando
                ? txt('Enviando validações...')
                : filaLimpa
                  ? txt('Tudo enviado ao servidor')
                  : pendentes === 1
                    ? txt('{quantas} validação por enviar', {quantas: pendentes})
                    : txt('{quantas} validações por enviar', {quantas: pendentes})}
            </Text>
            {!filaLimpa ? (
              <Text
                style={{
                  color: t.erroTinta,
                  fontSize: 13,
                  fontFamily: fontes.regular,
                  lineHeight: 18,
                }}>
                {txt('Não feche antes de esvaziar a fila.')}
              </Text>
            ) : null}
          </View>

          {enviando ? <ActivityIndicator color={t.primaria} /> : null}
        </View>

        {erroEnvio ? <Text style={[estilos.erro, {color: t.erro}]}>{erroEnvio}</Text> : null}

        <Text style={[estilos.rotulo, {color: t.textoFraco, marginTop: 2}]}>{txt('Por setor')}</Text>

        {relatorio && relatorio.porSetor.length > 0 ? (
          <View style={{gap: 12}}>
            {relatorio.porSetor.map((linha, indice) => (
              <View key={linha.setorId || `sem-${indice}`} style={{gap: 7}}>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                  }}>
                  <Text style={{color: t.texto, fontSize: 16, fontFamily: fontes.negrito}}>
                    {linha.nome}
                  </Text>
                  <Text style={[estilos.numero, {color: t.texto, fontSize: 18}]}>
                    {comMilhar(linha.quantidade)}
                  </Text>
                </View>
                <View
                  style={{
                    height: 10,
                    borderRadius: 10,
                    backgroundColor: t.superficieAlta,
                    overflow: 'hidden',
                  }}>
                  <View
                    style={{
                      height: 10,
                      borderRadius: 10,
                      backgroundColor: t.primaria,
                      width: `${Math.round((linha.quantidade / maior) * 100)}%`,
                    }}
                  />
                </View>
              </View>
            ))}
          </View>
        ) : (
          <Text style={[estilos.subtitulo, {color: t.textoFraco}]}>
            {txt('Nenhuma entrada validada por este aparelho neste evento.')}
          </Text>
        )}
      </ScrollView>

      <View
        style={{
          padding: 20,
          gap: 10,
          borderTopWidth: 1,
          borderTopColor: t.superficie,
        }}>
        {!filaLimpa ? (
          <Pressable
            onPress={() => enviarPendentes()}
            disabled={enviando}
            style={[
              estilos.botaoSecundario,
              {borderWidth: 2, borderColor: t.primaria, opacity: enviando ? 0.5 : 1},
            ]}>
            <CloudUpload size={19} color={t.primaria} strokeWidth={2.4} />
            <Text style={[estilos.textoBotaoSecundario, {color: t.primaria, fontSize: 17}]}>
              {txt('Enviar agora')}
            </Text>
          </Pressable>
        ) : null}

        <Pressable
          onPress={tentarFechar}
          disabled={enviando}
          style={[
            estilos.botao,
            {height: 56, backgroundColor: enviando ? t.superficieAlta : t.primaria},
          ]}>
          <Text
            style={[
              estilos.textoBotao,
              {fontSize: 17, color: enviando ? t.textoApagado : t.tintaPrimaria},
            ]}>
            {txt('Fechar')}
          </Text>
        </Pressable>

        <Pressable
          onPress={() => navigation.goBack()}
          style={[estilos.botaoSecundario, {height: 48, borderColor: t.borda}]}>
          <Text style={[estilos.textoBotaoSecundario, {color: t.textoFraco, fontSize: 15}]}>
            {txt('Continuar validando')}
          </Text>
        </Pressable>
      </View>
    </SafeAreaView>
  );
}
